import { IonContent, IonPage } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { usePublicReport } from '../features/publicReport/hooks/usePublicReport';
import { detectPublicLocale } from '../features/publicReport/localeUtils';
import PublicReportLoading from '../features/publicReport/components/PublicReportLoading';
import PublicCleaningReport from '../features/publicReport/components/PublicCleaningReport';
import PublicReportFallback from '../features/publicReport/components/PublicReportFallback';
import { isCurrentUserAuthenticated } from '@otuvy/auth';

interface PublicReportPageProps {
  listID: string;
}

/**
 * Public report page that can be rendered without authentication.
 *
 * Flow:
 * 1. Check authentication FIRST
 *    - If authenticated → redirect to share flow (list page)
 * 2. If not authenticated, call public report API
 *    - If API returns data → show the public report
 *    - If API fails → show fallback with app download links
 */
const PublicReportPage: React.FC<PublicReportPageProps> = ({ listID }) => {
  const locale = useMemo(() => detectPublicLocale(), []);

  // Check auth FIRST - authenticated users should always go to share flow
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Check auth status immediately on mount
  useEffect(() => {
    isCurrentUserAuthenticated().then(setIsAuthenticated);
  }, []);

  // Redirect authenticated users to the share flow immediately
  useEffect(() => {
    if (isAuthenticated === true) {
      // Redirect to share flow with internal flag to bypass public report interception
      window.location.href = `/qr/sh/list/${listID}?internal=true`;
    }
  }, [isAuthenticated, listID]);

  // Only fetch public report data if user is NOT authenticated
  const shouldFetchReport = isAuthenticated === false;
  const { data: publicReportData, loading: publicReportLoading } = usePublicReport(shouldFetchReport ? listID : '');

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <IonPage>
        <IonContent>
          <PublicReportLoading locale={locale} />
        </IonContent>
      </IonPage>
    );
  }

  // Authenticated users will be redirected - show loading during redirect
  if (isAuthenticated === true) {
    return (
      <IonPage>
        <IonContent>
          <PublicReportLoading locale={locale} />
        </IonContent>
      </IonPage>
    );
  }

  // Not authenticated - show loading while API is in progress
  if (publicReportLoading) {
    return (
      <IonPage>
        <IonContent>
          <PublicReportLoading locale={locale} />
        </IonContent>
      </IonPage>
    );
  }

  // Public report data available - show the report
  if (publicReportData) {
    return (
      <IonPage>
        <IonContent>
          <PublicCleaningReport report={publicReportData} locale={locale} />
        </IonContent>
      </IonPage>
    );
  }

  // Not authenticated and no report data - show fallback with app download links
  return (
    <IonPage>
      <IonContent>
        <PublicReportFallback locale={locale} />
      </IonContent>
    </IonPage>
  );
};

export default PublicReportPage;
